// ─── Task (issue #1680, epic #1674) ────────────────────────────────────────
//
// Write client for the generic, cross-pillar `Task` assignable-task
// primitive (issue #1678's REST surface). `output` is an untyped
// `Record<string, unknown>` — see `graphql/types/task.ts`'s note on why no
// `taskType`-specific shape exists to type against yet.

import type { Task } from '../../graphql/types/task';
import { apiRequest } from './http';

export interface UpdateTaskInput {
  name?: string;
  description?: string;
  status?: string;
  output?: Record<string, unknown>;
  resultEntityId?: string;
  responseCount?: number;
  completionPercentage?: number;
  overdue?: boolean;
}

export interface SubmitTaskResponseInput {
  respondentType: 'Team' | 'User';
  respondentId: string;
  output: Record<string, unknown>;
}

export interface TaskResponseRecord {
  entityId: string;
  entityType: string;
  taskId: string;
  respondentType: 'Team' | 'User';
  respondentId: string;
  output: Record<string, unknown>;
  respondedAt: string;
  _schemaVersion: string;
  _createdAt: string;
  _updatedAt: string;
}

/**
 * `PUT /api/v1/tasks/:id` — the assignee submits/updates the Task's output
 * (single-respondent inline path) and/or its status.
 */
export async function updateTask(taskId: string, input: UpdateTaskInput): Promise<Task> {
  return apiRequest<Task>(`/tasks/${taskId}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

/**
 * `POST /api/v1/tasks/:id/approve` — approve a `Complete` single-respondent
 * Task. Rejected by the backend (409) for a multi-respondent
 * (`audienceType` set) Task — see assignable-tasks-spec.md § Status lifecycle.
 */
export async function approveTask(taskId: string): Promise<Task> {
  return apiRequest<Task>(`/tasks/${taskId}/approve`, {
    method: 'POST',
  });
}

/**
 * `POST /api/v1/tasks/:id/activate` — the deliberate human activation step
 * (issue #1829) for a cadence-based or Staleness-triggered `Draft` Task:
 * moves it to `Open`, making it reachable through the respond UI
 * (`isRespondable`, `frontend/src/routes/tasks/$taskId.tsx`). Rejected by
 * the backend (409) for a Task not currently `Draft`.
 */
export async function activateTask(taskId: string): Promise<Task> {
  return apiRequest<Task>(`/tasks/${taskId}/activate`, {
    method: 'POST',
  });
}

/**
 * `POST /api/v1/tasks/:id/responses` — submit (or replace) a `TaskResponse`
 * on a multi-respondent (`audienceType` set) Task.
 */
export async function submitTaskResponse(
  taskId: string,
  input: SubmitTaskResponseInput
): Promise<TaskResponseRecord> {
  return apiRequest<TaskResponseRecord>(`/tasks/${taskId}/responses`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
